import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  DocumentData
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { debug } from '../utils/debug';
import { v4 as uuidv4 } from 'uuid';
import { hashEmail, normalizeEmail } from '../utils/crypto';

// Configuration API
export const getConfiguration = async () => {
  try {
    const configDoc = await getDoc(doc(db, 'configurations', 'site'));
    if (configDoc.exists()) {
      return configDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting configuration:', error);
    throw error;
  }
};

export const updateConfiguration = async (configData: any) => {
  try {
    await updateDoc(doc(db, 'configurations', 'site'), {
      ...configData,
      lastUpdated: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error updating configuration:', error);
    throw error;
  }
};

// Subscribers API

/**
 * Check if an email already exists by hashing it and querying the public collection
 * @param email The email address to check
 * @returns A promise that resolves to a boolean indicating if the email exists
 */
export const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    debug.log(`Checking if email exists (client-side): ${email} (Type: ${typeof email})`);
    
    // Ensure email is a string and not empty
    if (!email || typeof email !== 'string') {
      debug.error('Invalid email parameter:', { email, type: typeof email });
      return false;
    }
    
    // Hash the email for secure lookup
    const emailHash = await hashEmail(email);
    debug.log('Email hash generated for lookup:', emailHash);
    
    // Query the public collection for the email hash
    const tokensRef = collection(db, 'subscriber_tokens');
    const q = query(tokensRef, where('emailHash', '==', emailHash), limit(1));
    const querySnapshot = await getDocs(q);
    
    const exists = !querySnapshot.empty;
    debug.log('Email exists check result:', { exists });
    
    return exists;
  } catch (error) {
    console.error('Error checking if email exists:', error);
    debug.error('Error checking if email exists:', error);
    
    // Default to false on error to allow the user to try subscribing
    return false;
  }
};

/**
 * Add a new subscriber using the secure two-collection approach
 * @param email The email address of the subscriber
 * @param language The language preference of the subscriber
 * @param metadata Additional metadata about the subscriber
 * @returns A promise that resolves to the tokenId of the new subscriber
 */
export const addSubscriber = async (email: string, language: string, metadata: any) => {
  try {
    // First check if the email already exists
    const emailExists = await checkEmailExists(email);
    
    if (emailExists) {
      debug.log('Email already exists, cannot subscribe:', email);
      throw new Error('Email already subscribed');
    }
    
    // Generate a unique token ID
    const tokenId = uuidv4();
    debug.log('Generated unique token ID:', tokenId);
    
    // Hash the email for public storage
    const emailHash = await hashEmail(email);
    debug.log('Email hash generated for storage:', emailHash);
    
    // Enhance metadata with browser and device information
    const enhancedMetadata = {
      // Source info
      source: metadata.source || window.location.href,
      referrer: metadata.referrer || document.referrer,
      
      // Browser and device info
      userAgent: metadata.userAgent || navigator.userAgent,
      language: metadata.language || navigator.language,
      platform: metadata.platform || navigator.platform,
      vendor: metadata.vendor || navigator.vendor,
      screenWidth: metadata.screenWidth || window.screen.width.toString(),
      screenHeight: metadata.screenHeight || window.screen.height.toString(),
      timezone: metadata.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      browserLanguage: navigator.language,
      browserLanguages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
      
      // Location info (might be empty if location fetch failed)
      country: metadata.country || '',
      region: metadata.region || '',
      ip: metadata.ip || '',
      
      // UTM parameters
      utm_source: metadata.utm_source || '',
      utm_medium: metadata.utm_medium || '',
      utm_campaign: metadata.utm_campaign || '',
      
      // Timestamps
      createdAt: new Date().toISOString(),
      timestamp: metadata.timestamp || new Date().toISOString()
    };
    
    debug.log('Enhanced Metadata prepared');
    
    // Create the public token record with limited metadata (no PII)
    const tokenRecord = {
      tokenId,
      emailHash,
      language: language || 'en',
      status: 'pending_verification', // Encoded status
      createdAt: Timestamp.now(),
      metadata: {
        // Include only non-PII metadata
        source: enhancedMetadata.source,
        referrer: enhancedMetadata.referrer,
        platform: enhancedMetadata.platform,
        screenWidth: enhancedMetadata.screenWidth,
        screenHeight: enhancedMetadata.screenHeight,
        timezone: enhancedMetadata.timezone,
        country: enhancedMetadata.country,
        region: enhancedMetadata.region,
        utm_source: enhancedMetadata.utm_source,
        utm_medium: enhancedMetadata.utm_medium,
        utm_campaign: enhancedMetadata.utm_campaign,
        createdAt: enhancedMetadata.createdAt
      }
    };
    
    // Create the private subscriber record with full data
    const subscriberRecord = {
      tokenId,
      email: normalizeEmail(email),
      subscriptionDate: Timestamp.now(),
      status: 'active',
      verified: false,
      language: language || 'en',
      metadata: enhancedMetadata
    };
    
    debug.log('Prepared records for both collections');
    
    // Add the public token record
    const tokensRef = collection(db, 'subscriber_tokens');
    await addDoc(tokensRef, tokenRecord);
    debug.log('Added public token record');
    
    // Add the private subscriber record
    const subscribersRef = collection(db, 'subscribers');
    const docRef = await addDoc(subscribersRef, subscriberRecord);
    debug.log('Added private subscriber record with ID:', docRef.id);
    
    debug.log('Subscriber added successfully with token:', { tokenId });
    return tokenId;
  } catch (error) {
    console.error('Error adding subscriber:', error);
    debug.error('Error adding subscriber:', error);
    throw error;
  }
};

export const getSubscribers = async (pageSize = 10, startAfterDoc: any = null, filterOptions: any = {}) => {
  try {
    const subscribersRef = collection(db, 'subscribers');
    let q = query(subscribersRef, orderBy('subscriptionDate', 'desc'), limit(pageSize));
    
    // Apply filters if provided
    if (filterOptions.status) {
      q = query(q, where('status', '==', filterOptions.status));
    }
    
    if (filterOptions.verified !== undefined) {
      q = query(q, where('verified', '==', filterOptions.verified));
    }
    
    // Apply pagination
    if (startAfterDoc) {
      q = query(q, startAfter(startAfterDoc));
    }
    
    const querySnapshot = await getDocs(q);
    const subscribers: DocumentData[] = [];
    
    querySnapshot.forEach((doc) => {
      subscribers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      subscribers,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
    };
  } catch (error) {
    console.error('Error getting subscribers:', error);
    throw error;
  }
};

export const updateSubscriber = async (subscriberId: string, data: any) => {
  try {
    // Get the subscriber document to find the tokenId
    const subscriberDoc = await getDoc(doc(db, 'subscribers', subscriberId));
    
    if (!subscriberDoc.exists()) {
      throw new Error('Subscriber not found');
    }
    
    const subscriberData = subscriberDoc.data();
    const tokenId = subscriberData.tokenId;
    
    // Update the subscriber document
    await updateDoc(doc(db, 'subscribers', subscriberId), {
      ...data,
      lastUpdated: Timestamp.now()
    });
    debug.log('Updated subscriber document:', subscriberId);
    
    // If there's a tokenId and we're updating status or language, update the token document too
    if (tokenId && (data.status || data.language)) {
      const tokensRef = collection(db, 'subscriber_tokens');
      const q = query(tokensRef, where('tokenId', '==', tokenId), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const tokenDoc = querySnapshot.docs[0];
        const tokenUpdateData: any = {};
        
        // Only include fields that are being updated
        if (data.status) {
          // Map the clear status to an encoded status for the public collection
          const statusMap: {[key: string]: string} = {
            'active': 'active',
            'unsubscribed': 'unsub'
          };
          tokenUpdateData.status = statusMap[data.status] || data.status;
        }
        
        if (data.language) {
          tokenUpdateData.language = data.language;
        }
        
        await updateDoc(doc(db, 'subscriber_tokens', tokenDoc.id), tokenUpdateData);
        debug.log('Updated token document for tokenId:', tokenId);
      } else {
        debug.warn('Token document not found for tokenId:', tokenId);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error updating subscriber:', error);
    debug.error('Error updating subscriber:', error);
    throw error;
  }
};

// Verify a subscriber's email
export const verifySubscriberEmail = async (subscriberId: string) => {
  try {
    // Get the subscriber document to find the tokenId
    const subscriberDoc = await getDoc(doc(db, 'subscribers', subscriberId));
    
    if (!subscriberDoc.exists()) {
      throw new Error('Subscriber not found');
    }
    
    const subscriberData = subscriberDoc.data();
    const tokenId = subscriberData.tokenId;
    
    if (!tokenId) {
      debug.error('Subscriber has no tokenId:', subscriberId);
      throw new Error('Subscriber has no associated token');
    }
    
    // Update the subscriber document
    await updateDoc(doc(db, 'subscribers', subscriberId), {
      verified: true,
      verifiedAt: Timestamp.now()
    });
    
    // Find the token document using a query
    const tokensRef = collection(db, 'subscriber_tokens');
    const q = query(tokensRef, where('tokenId', '==', tokenId), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      debug.warn('Token document not found for tokenId:', tokenId);
    } else {
      // Update the token document
      const tokenDoc = querySnapshot.docs[0];
      await updateDoc(doc(db, 'subscriber_tokens', tokenDoc.id), {
        status: 'verified'
      });
      debug.log('Updated token document status to verified');
    }
    
    return true;
  } catch (error) {
    console.error('Error verifying subscriber email:', error);
    debug.error('Error verifying subscriber email:', error);
    throw error;
  }
};

export const deleteSubscriber = async (subscriberId: string) => {
  try {
    // Get the subscriber document to find the tokenId
    const subscriberDoc = await getDoc(doc(db, 'subscribers', subscriberId));
    
    if (!subscriberDoc.exists()) {
      throw new Error('Subscriber not found');
    }
    
    const subscriberData = subscriberDoc.data();
    const tokenId = subscriberData.tokenId;
    
    // Delete the subscriber document
    await deleteDoc(doc(db, 'subscribers', subscriberId));
    debug.log('Deleted subscriber document:', subscriberId);
    
    // If there's a tokenId, find and delete the token document
    if (tokenId) {
      const tokensRef = collection(db, 'subscriber_tokens');
      const q = query(tokensRef, where('tokenId', '==', tokenId), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const tokenDoc = querySnapshot.docs[0];
        await deleteDoc(doc(db, 'subscriber_tokens', tokenDoc.id));
        debug.log('Deleted token document for tokenId:', tokenId);
      } else {
        debug.warn('Token document not found for tokenId:', tokenId);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting subscriber:', error);
    debug.error('Error deleting subscriber:', error);
    throw error;
  }
};

// Translations API
export const getTranslations = async (languageCode: string) => {
  try {
    const langDoc = await getDoc(doc(db, 'languages', languageCode));
    if (langDoc.exists()) {
      return langDoc.data();
    }
    return null;
  } catch (error) {
    console.error(`Error getting translations for ${languageCode}:`, error);
    throw error;
  }
};

export const updateTranslations = async (languageCode: string, translations: any) => {
  try {
    await updateDoc(doc(db, 'languages', languageCode), translations);
    return true;
  } catch (error) {
    console.error(`Error updating translations for ${languageCode}:`, error);
    throw error;
  }
};

// Communications API
export const createCommunication = async (communicationData: any) => {
  try {
    const communicationsRef = collection(db, 'communications');
    const docRef = await addDoc(communicationsRef, {
      ...communicationData,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating communication:', error);
    throw error;
  }
};

export const getCommunications = async () => {
  try {
    const communicationsRef = collection(db, 'communications');
    const q = query(communicationsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const communications: DocumentData[] = [];
    querySnapshot.forEach((doc) => {
      communications.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return communications;
  } catch (error) {
    console.error('Error getting communications:', error);
    throw error;
  }
};